"use client";

import { useEffect, useId, useRef } from "react";

const DEPTH_KEY = "modalDepth";
const OWNER_KEY = "modalOwner";

// How many open modals hold a history entry, so stacked modals close one per Back press.
let openDepth = 0;
// Dropping an entry waits a tick, so a modal opening in that tick (React Strict Mode) can keep it.
let pendingBack: ReturnType<typeof setTimeout> | undefined;

/**
 * While `active`, adds a history entry so the browser Back button calls `onClose` instead of
 * leaving the page. Closing any other way removes the entry again, and Forward back onto it
 * calls `onReopen`. Not for modals that navigate as they close: removing the entry sends the
 * router a Back, which cancels the navigation.
 */
export default function useCloseOnBrowserBack(active: boolean, onClose: () => void, onReopen?: () => void): void {
  const owner = useId();
  const onCloseRef = useRef(onClose);
  const onReopenRef = useRef(onReopen);
  useEffect(() => {
    onCloseRef.current = onClose;
    onReopenRef.current = onReopen;
  }, [onClose, onReopen]);

  useEffect(() => {
    if (!active) {
      return;
    }

    openDepth += 1;
    const depth = openDepth;
    const entry = readEntry(window.history.state);
    if (entry.depth === depth && (pendingBack !== undefined || entry.owner === owner)) {
      // Opened again in the same tick (React Strict Mode) or by Forward: the entry is already here.
      clearTimeout(pendingBack);
      pendingBack = undefined;
      if (entry.owner !== owner) {
        window.history.replaceState({ ...window.history.state, [OWNER_KEY]: owner }, "");
      }
    } else {
      window.history.pushState({ [DEPTH_KEY]: depth, [OWNER_KEY]: owner }, "");
    }

    let closedByBack = false;
    const handlePopState = (event: PopStateEvent) => {
      if (readEntry(event.state).depth < depth) {
        closedByBack = true;
        onCloseRef.current();
      }
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      openDepth -= 1;
      // After a navigation the current entry belongs to the new page, so history is left alone.
      // Checked again when the timer runs, in case a navigation lands in between.
      if (!closedByBack && readEntry(window.history.state).depth === depth) {
        pendingBack = setTimeout(() => {
          pendingBack = undefined;
          const current = readEntry(window.history.state);
          if (current.depth === depth && current.owner === owner) {
            window.history.back();
          }
        });
      }
    };
  }, [active, owner]);

  // Forward onto the entry this modal left behind brings it back.
  useEffect(() => {
    if (active) {
      return;
    }
    const handlePopState = (event: PopStateEvent) => {
      if (readEntry(event.state).owner === owner) {
        onReopenRef.current?.();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [active, owner]);
}

function readEntry(state: unknown): { depth: number; owner?: string } {
  const entry = (state ?? {}) as Record<string, unknown>;
  const depth = entry[DEPTH_KEY];
  const owner = entry[OWNER_KEY];
  return { depth: typeof depth === "number" ? depth : 0, owner: typeof owner === "string" ? owner : undefined };
}
